import { isExplicitDesiredDate } from 'src/stores/_infrastructure/desiredDateFilter';
import { useDocumentListStore } from 'src/stores/documentList';
import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { DateRangeSelection } from 'src/models/common';
import { ActivityListTypeEnum, MODULE_LIST_TYPE } from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { StatusEnum } from 'src/models/status/enums';

import { today } from 'src/util/date';
import { registerListInstanceFetcher, resolveListInstance } from 'src/util/listInstance/listInstanceFetchDispatch';
import { registerModuleTabSettingsReset } from 'src/util/listInstance/moduleTabSettingsReset';
import { QueryStore } from 'src/util/query/QueryStore';

const warmupDocumentList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	}
) => {
	const documentListStore = useDocumentListStore();

	void documentListStore.fetchFilterBoxes({
		queryStore: queryStore,
	});

	void documentListStore.fetchFilterUsers({
		queryStore: queryStore,
	});

	void fetchDocumentList(_store, queryStore, {
		...queryParameters,
		clearResultsBeforeFetch: false,
	});
};

const fetchDocumentList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		clearResultsBeforeFetch: boolean;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	},
	listStore: ReturnType<typeof useDocumentListStore> = useDocumentListStore()
) => {
	const hasActiveSearch = queryParameters.searchTerm.trim() !== '' || queryParameters.hiddenSearchTerm.trim() !== '';
	const didClearSubscriptionsForSearch = hasActiveSearch;

	if (isExplicitDesiredDate(queryParameters.desiredDate)) {
		listStore.setDateFilterAction({
			dateFilter: queryParameters.desiredDate,
		});
	}

	if (hasActiveSearch) {
		listStore.clearSubscriptions({
			queryStore: queryStore,
		});
	}

	if (queryParameters.clearResultsBeforeFetch) {
		if (
			queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetOpenDocumentList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.MINE ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetMineDocumentList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.ALL ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetAllDocumentList();
		}
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchOpenDocumentList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.MINE ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchMineDocumentList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.ALL ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchAllDocumentList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}
};

registerListInstanceFetcher('documentList', ({ listStore, params, queryStore, runtimeStore }) =>
	fetchDocumentList(runtimeStore as never, queryStore as never, params as never, listStore as never)
);

// Task 2.4 (analog Task 2.1/2.3): Basis-Identifier der drei Sub-Listen-Scroll-Positionen (Offen/Meine/Alle)
// — muss `DocumentList.vue`s `virtual-list-id="moduleActivityListScrollerDocuments"` spiegeln (dort mit
// Open/Mine/All-Suffix versehen, s. GenericActivityListTabPanel.vue). Kein zentraler Export vorhanden,
// daher hier dieselbe Literal-Konstante wie in DocumentList.vue.
const DOCUMENT_LIST_SCROLL_BASE_IDENTIFIER = 'moduleActivityListScrollerDocuments';

// Nur die Default-Instanz (Modul-Tabs laufen immer auf ihr) — `?list=`-Instanz-Tabs disposen bereits
// vollständig über useListInstanceLifecycle und laufen nie durch diesen Reset (Task 1.7 im Layout-Hook
// filtert sie über tab.query.list aus). Feldliste 1:1 aus clearAllFilters/selectTheSharedQuery
// (FilterComponentActions.ts) für 'documentList' abgeleitet, OHNE deren router.replace/refreshTheList:
// Box-/Benutzer-Filter, Datumsfilter (+ Zeitraum-Preset), gewählte Shared-Query, ido-Dokument-Bindung
// (idoDocumentBinding — von selectTheSharedQuery gesetzt/gelöscht, transient wie die Shared-Query-Auswahl
// selbst; siehe FilterComponentActions.ts clearAllFilters, das dieselbe Bindung ebenfalls löscht), Such-/
// Hidden-Suchbegriff, Mehrfachauswahl, Scroll-Positionen der drei Sub-Listen, aktive Ansicht. `documentList`
// hat keine Richtungs-Filter (isDirectionRoute() schließt es aus) — kein Reset-Ziel dafür.
// NICHT angefasst: sort/listViewModeLarge (persistierte Präferenzen), Datenlisten + Counts + Pagination +
// queryStoreSubscriptions (Menü-Badges/Warmup lesen den Default-Store weiter — kein $reset/$dispose).
// standardBox EXPLIZIT NICHT angefasst: kein Filter, sondern geladene Konfiguration (das Standard-Postfach
// des Users, via fetchStandardBox()/GetTheStandardDocumentModuleBoxForTheUserQuery) — analog zu
// filterBoxes/filterUsers (den verfügbaren Optionen, nicht der aktiven Auswahl), die andere Module-Resets
// ebenfalls nicht anfassen. DocumentAddDialog.vue liest es vom Default-Store weiter (Brief).
const resetDocumentListModuleTabSettings = (): void => {
	const documentListStore = useDocumentListStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();

	documentListStore.setFilteredBoxesAction({ boxIdFilter: [] });
	documentListStore.setFilteredUsersAction({ filterUsers: [] });
	documentListStore.setDateFilterAction({ dateFilter: today() });
	documentListStore.setDateTimeRangeFilter({ dateTimeRangeFilter: PreconfiguredTimeRanges.TODAY });
	documentListStore.setSelectedSharedQueryAction({ selectedSharedQuery: null });
	documentListStore.setIdoDocumentBinding(null);

	filterStore.setSearchTerm({ module: 'documentList', searchTerm: '' });
	filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'documentList' });

	genericActivityListStore.clearTheSelection({ genericListIdentifier: 'documentList' });
	genericActivityListStore.resetScrollPositions(DOCUMENT_LIST_SCROLL_BASE_IDENTIFIER);
	genericActivityListStore.setActiveView({
		activeView: MODULE_LIST_TYPE.OPEN,
		genericListIdentifier: 'documentList',
	});
};

registerModuleTabSettingsReset('documentList', resetDocumentListModuleTabSettings);

const deleteTheSelectedDocumentAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: { aggregateIds: string[]; genericListIdentifier?: string; processId: string }
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const documentListStore: ReturnType<typeof useDocumentListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useDocumentListStore();

		documentListStore.deleteSelectedAggregateIds({
			aggregateIds: queryParameters.aggregateIds,
		});
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'documentList',
	});
};

const setTheStateForTheSelectedDocumentAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: {
		aggregateIds: string[];
		genericListIdentifier?: string;
		state: StatusEnum;
	}
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const documentListStore: ReturnType<typeof useDocumentListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useDocumentListStore();

		switch (queryParameters.state) {
			case StatusEnum.UNSEEN:
				documentListStore.setTheDocumentStateToUnseen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.SEEN:
				documentListStore.setTheDocumentStateToSeen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.READY:
				documentListStore.setTheDocumentStateToReady({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			default:
				break;
		}
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'documentList',
	});
};

export {
	deleteTheSelectedDocumentAggregateIds,
	fetchDocumentList,
	setTheStateForTheSelectedDocumentAggregateIds,
	warmupDocumentList,
};
